import { Action, Selector, State } from '@ngxs/store';
import type { StateContext } from '@ngxs/store';
import { inject, Injectable } from '@angular/core';
import { DataUploadService } from '../../services/data-upload.service';
import {
    AbortLatestUpload,
    CancelFleetUpload,
    ConfirmFleetUpload,
    LoadCurrentUploads,
    LoadUploadLink,
    UploadFile
} from './data-upload.actions';
import { catchError, EMPTY, finalize, Observable, switchMap, tap, throwError } from 'rxjs';
import { IUpload } from '@monorepo/interfaces';
import { FleetManagementService } from '../../../fleet-management/services/fleet-management.service';

export interface DataUploadStateModel {
    uploadLink: string | undefined;
    currentUploads: IUpload[];
    isLoadingLink: boolean;
    isUploading: boolean;
    isInitialLoad: boolean;
    isUploadLinkBeingCalled: boolean;
}

@State<DataUploadStateModel>({
    name: 'dataUpload',
    defaults: {
        uploadLink: undefined,
        currentUploads: [],
        isLoadingLink: false,
        isUploading: false,
        isInitialLoad: false,
        isUploadLinkBeingCalled: false
    }
})
@Injectable()
export class DataUploadState {
    private dataUploadService = inject(DataUploadService);
    private fleetManagementService = inject(FleetManagementService);

    @Selector()
    static uploadLink(state: DataUploadStateModel): string | undefined {
        return state.uploadLink;
    }

    @Selector()
    static currentUploads(state: DataUploadStateModel): IUpload[] {
        return state.currentUploads;
    }

    @Selector()
    static isLoadingLink(state: DataUploadStateModel): boolean {
        return state.isLoadingLink;
    }

    @Selector()
    static isUploading(state: DataUploadStateModel): boolean {
        return state.isUploading;
    }

    @Selector()
    static isInitialLoad(state: DataUploadStateModel): boolean {
        return state.isInitialLoad;
    }

    @Selector()
    static isUploadLinkBeingCalled(state: DataUploadStateModel): boolean {
        return state.isUploadLinkBeingCalled;
    }

    @Action(LoadUploadLink)
    loadUploadLink(ctx: StateContext<DataUploadStateModel>, action: LoadUploadLink): Observable<{ signedURL: string }> {
        this.setIsLoading(ctx, true);
        return this.dataUploadService.getUploadLink(action.fileType).pipe(
            tap(uploadLink => ctx.patchState({ uploadLink: uploadLink.signedURL })),
            finalize(() => {
                this.setIsLoading(ctx, false);
                this.setIsUploadLinkBeingCalled(ctx, true);
            })
        );
    }

    @Action(LoadCurrentUploads)
    loadCurrentUploads(ctx: StateContext<DataUploadStateModel>, action: LoadCurrentUploads): Observable<IUpload[]> {
        if (action.isInitial) {
            this.setIsInitialLoading(ctx, true);
        }
        this.setIsLoading(ctx, true);
        return this.dataUploadService.getCurrentUploads(action.fileType).pipe(
            tap(currentUploads => ctx.patchState({ currentUploads })),
            finalize(() => {
                this.setIsLoading(ctx, false);
                this.setIsInitialLoading(ctx, false);
            })
        );
    }

    @Action(UploadFile)
    uploadFile(ctx: StateContext<DataUploadStateModel>, action: UploadFile): Observable<void> {
        this.setIsUploading(ctx, true);
        return this.dataUploadService.uploadFile(action.file, action.uploadLink).pipe(
            tap(() => {
                ctx.patchState({ uploadLink: undefined });
            }),
            catchError(error => {
                ctx.patchState({ uploadLink: undefined });
                return throwError(() => error);
            }),
            finalize(() => {
                this.setIsUploading(ctx, false);
                this.setIsUploadLinkBeingCalled(ctx, false);
            })
        );
    }

    @Action(AbortLatestUpload)
    abortLatestUpload(ctx: StateContext<DataUploadStateModel>, action: AbortLatestUpload) {
        ctx.patchState({ isUploading: false, isUploadLinkBeingCalled: false, uploadLink: undefined });
        return this.dataUploadService.getCurrentUploads(action.fileType).pipe(
            switchMap(currentUploads => {
                const latestUpload = currentUploads[0];
                if (latestUpload) {
                    return this.dataUploadService.abortUpload(latestUpload.id);
                }
                return EMPTY;
            })
        );
    }

    @Action(ConfirmFleetUpload)
    confirmFleetUpload(
        ctx: StateContext<DataUploadStateModel>,
        action: ConfirmFleetUpload
    ): Observable<{ message: string }> {
        return this.fleetManagementService.confirmFleetUpload(action.uploadId);
    }

    @Action(CancelFleetUpload)
    cancelFleetUpload(
        ctx: StateContext<DataUploadStateModel>,
        action: CancelFleetUpload
    ): Observable<{ message: string }> {
        return this.fleetManagementService.cancelFleetUpload(action.uploadId);
    }

    private setIsLoading(ctx: StateContext<DataUploadStateModel>, isLoading: boolean): void {
        ctx.patchState({ isLoadingLink: isLoading });
    }

    private setIsInitialLoading(ctx: StateContext<DataUploadStateModel>, isInitial: boolean): void {
        ctx.patchState({ isInitialLoad: isInitial });
    }

    private setIsUploading(ctx: StateContext<DataUploadStateModel>, isUploading: boolean): void {
        ctx.patchState({ isUploading });
    }

    private setIsUploadLinkBeingCalled(
        ctx: StateContext<DataUploadStateModel>,
        isUploadLinkBeingCalled: boolean
    ): void {
        ctx.patchState({ isUploadLinkBeingCalled });
    }
}
